import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Platform, AppState, DeviceEventEmitter } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import conversationService, { ConversationMessage, ConversationResponse } from '../services/conversationService';
import conversationSummaryService from '../services/conversationSummaryService';
import { sobrietyMilestoneService } from '../services/sobrietyMilestoneService';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { billConfigCacheService } from '../services/billConfigCacheService';
import { formatPageNames } from '../utils/pageNameFormatter';
import onboardingService from '../services/onboardingService';
import contextPersistenceService from '../services/contextPersistenceService';

// memoryMonitorService not available in this environment

export interface BillContextType {
  messages: ConversationMessage[];
  conversationHistory: ConversationMessage[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  dailyLimitReached: boolean;
  remainingMessages: number;
  sendMessage: (message: string, specificGuidance?: string, sobrietyDays?: number) => Promise<void>;
  sendDirectMessage: (message: string, specificGuidance?: string, sobrietyDays?: number) => Promise<void>;
  loadConversationHistory: () => Promise<boolean>;
  clearConversation: () => Promise<void>;
  updateTypingActivity: () => void;
  isInitialized: boolean;
  hasHistoricalConversations: boolean;
  isTyping: boolean;
  // REMOVED: pendingMessages - no longer using batching system
  isBatchProcessing: boolean;
  checkSobrietyMilestone: (currentDays: number, previousDays?: number) => Promise<void>;
  getDailyEncouragement: () => Promise<void>;
  getCrisisSupport: () => Promise<void>;
  resetDailyLimit: () => void;
  checkDailyLimitExpiration: () => Promise<boolean>;
}

const BillContext = createContext<BillContextType | undefined>(undefined);

const STORAGE_KEY = 'bill_messages';
const MAX_STORED_MESSAGES = 50;
const CLEANUP_INTERVAL = 600000; // 10 minutes

// Helper function to split Bill's response into multiple messages
const splitBillResponse = (response: string): string[] => {
  // Format page paths before splitting (e.g., "/diary" → "<b>Diário</b>")
  const formattedResponse = formatPageNames(response);
  
  // First, check if response already has the ||| separator
  if (formattedResponse.includes('|||')) {
    return formattedResponse.split('|||').map(msg => msg.trim()).filter(msg => msg.length > 0);
  }

  // If no separator, split intelligently based on content length and structure
  // Don't split on numbered lists (1., 2., 3.) or abbreviations
  // Enhanced regex: avoids splitting after "digit." patterns (e.g., "1.", "2.", "3.")
  const sentences = formattedResponse.split(/(?<!\d\.)(?<=[.!?])\s+/);
  const messages: string[] = [];
  let currentMessage = '';

  const MAX_MESSAGE_LENGTH = 150; // Characters per message card
  const MIN_MESSAGE_LENGTH = 50;  // Minimum characters before forcing split

  for (const sentence of sentences) {
    const potentialMessage = currentMessage + (currentMessage ? ' ' : '') + sentence;

    if (potentialMessage.length <= MAX_MESSAGE_LENGTH) {
      currentMessage = potentialMessage;
    } else {
      // If current message is long enough, save it and start new one
      if (currentMessage.length >= MIN_MESSAGE_LENGTH) {
        messages.push(currentMessage.trim());
        currentMessage = sentence;
      } else {
        // If current message is too short, keep adding
        currentMessage = potentialMessage;
      }
    }
  }

  // Add remaining message
  if (currentMessage.trim()) {
    messages.push(currentMessage.trim());
  }

  // Ensure we have at least one message
  if (messages.length === 0) {
    messages.push(response);
  }

  // Allow unlimited message cards to show complete responses
  // Removed arbitrary 4-balloon limit to prevent content truncation
  return messages;
};

// Async queue for saving conversations - now uses API with JWT auth
const conversationSaveQueue: Array<{ user_id: string; user_message: string; bill_response: string; created_at: string; access_token: string }> = [];
let isSavingConversation = false;

const processConversationSaveQueue = async () => {
  if (isSavingConversation || conversationSaveQueue.length === 0) {
    return;
  }
  isSavingConversation = true;

  const conversationData = conversationSaveQueue.shift();

  if (!conversationData) {
    isSavingConversation = false;
    return;
  }

  try {
    console.log('💾 BillContext: Saving conversation via API (async)...', {
      userId: conversationData.user_id,
      userMessagePreview: conversationData.user_message.substring(0, 50) + '...',
      billResponsePreview: conversationData.bill_response.substring(0, 100) + '...',
      createdAt: conversationData.created_at
    });

    const response = await fetch('https://mobile.padrinho.app/api/bill/conversations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${conversationData.access_token}`
      },
      body: JSON.stringify({
        user_message: conversationData.user_message,
        bill_response: conversationData.bill_response,
        created_at: conversationData.created_at
      })
    });

    if (!response.ok) {
      console.error('❌ BillContext: API error saving conversation:', response.status);
      const errorText = await response.text();
      console.error('❌ BillContext: Error details:', errorText);
    } else {
      const result = await response.json();
      console.log('✅ BillContext: Conversation saved via API successfully!', {
        recordId: result.conversation?.id,
        createdAt: result.conversation?.created_at,
        userId: conversationData.user_id
      });
    }
  } catch (saveError) {
    console.error('❌ BillContext: Unexpected error during API save:', saveError);
  } finally {
    isSavingConversation = false;
    // Process next item in queue immediately if available
    processConversationSaveQueue();
  }
};

export function BillProvider({ children }: { children: React.ReactNode }) {
  // CRITICAL FIX: Get session and sessionReady from AuthContext
  // sessionReady indicates the session has been validated and is safe for API calls
  const { user, session, sessionReady } = useAuth();
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState(30); // Server-provided remaining messages count
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasHistoricalConversations, setHasHistoricalConversations] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  // REMOVED: pendingMessages - no longer using batching system
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  const initializationRef = useRef(false);
  const pendingBatchRef = useRef<ConversationMessage[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const userSentCountRef = useRef<number>(0); // counts user messages sent this session
  const userIdRef = useRef<string | undefined>(user?.id);
  // Mirror of `messages` so callbacks can read the latest array synchronously
  // (used to persist the full exchange eagerly, decoupled from the UI reveal).
  const messagesRef = useRef<ConversationMessage[]>([]);
  // Tracks balloons still waiting to be revealed so they can be flushed
  // immediately if the app is backgrounded mid-reveal (otherwise the JS timer
  // is suspended and the remaining balloons never render).
  const pendingRevealRef = useRef<{ remaining: ConversationMessage[]; timer: ReturnType<typeof setTimeout> | null } | null>(null);
  // Keep userIdRef in sync with user.id so callbacks without user in deps can read it
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  // Keep messagesRef in sync so eager persistence can read the latest array
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Fetch message usage from server API (source of truth)
  // Uses session from AuthContext to avoid getSession() timeout issues
  const fetchMessageUsageFromServer = useCallback(async (): Promise<{ limitReached: boolean; remainingMessages: number } | undefined> => {
    if (!user?.id) return;
    
    // Use session from AuthContext (instant, no network call)
    if (!session?.access_token) {
      console.warn('⚠️ BillContext: No session available for fetching message usage');
      return;
    }

    try {
      console.log('📊 BillContext: Fetching message usage from server...');
      const response = await fetch('https://mobile.padrinho.app/api/bill/message-usage', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        if (response.ok && result.success) {
          console.log('✅ BillContext: Server message usage:', {
            isPlusUser: result.isPlusUser,
            remaining: result.remainingMessages,
            limitReached: result.limitReached
          });
          
          // Update state from server (source of truth)
          if (result.isPlusUser) {
            // Plus user - no limits
            setDailyLimitReached(false);
            setRemainingMessages(-1); // Unlimited
          } else {
            // Base user - apply server limits
            setDailyLimitReached(result.limitReached);
            setRemainingMessages(result.remainingMessages);
          }
          return { limitReached: result.limitReached, remainingMessages: result.remainingMessages };
        } else {
          console.warn('⚠️ BillContext: Server returned error:', result);
        }
      }
      console.warn('⚠️ BillContext: Failed to fetch message usage from server');
    } catch (error) {
      console.error('❌ BillContext: Error fetching message usage:', error);
    }
  }, [user?.id, session?.access_token]);

  // Load message usage from server on mount and when user/session changes
  useEffect(() => {
    if (!user?.id) {
      setDailyLimitReached(false);
      setRemainingMessages(30);
      return;
    }
    
    // Wait for session to be validated before fetching
    if (!sessionReady || !session?.access_token) {
      console.log('📊 BillContext: User available but waiting for session validation...');
      return;
    }
    
    // Call immediately when session is validated and ready
    console.log('📊 BillContext: Session validated, fetching initial message usage...');
    fetchMessageUsageFromServer();
  }, [user?.id, session?.access_token, sessionReady, fetchMessageUsageFromServer]);

  // Refresh message usage when app resumes from background
  useEffect(() => {
    if (!user?.id) return;

    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active') {
        console.log('📊 BillContext: App resumed - refreshing message usage from server');
        fetchMessageUsageFromServer();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [user?.id, fetchMessageUsageFromServer]);

  // Remove batching - immediate response
  const USER_STOP_TYPING_DELAY = 0; // No delay - immediate processing
  // Helper method to find the latest complete conversation exchange
  const findLatestConversationExchange = (messages: ConversationMessage[]): {
    userMessage: ConversationMessage;
    assistantMessages: ConversationMessage[];
    exchangeStartIndex: number;
  } | null => {
    // Work backwards from the end to find the most recent user->assistant(s) pattern
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];

      if (message.role === 'user') {
        // Found a user message, now collect all following assistant messages
        const assistantMessages: ConversationMessage[] = [];

        for (let j = i + 1; j < messages.length; j++) {
          if (messages[j].role === 'assistant') {
            assistantMessages.push(messages[j]);
          } else {
            // Stop if we hit another user message
            break;
          }
        }

        // Only return if we have at least one assistant response
        if (assistantMessages.length > 0) {
          return {
            userMessage: message,
            assistantMessages,
            exchangeStartIndex: i
          };
        }
      }
    }

    return null;
  };

  // Load conversation history from API and convert to messages format
  // Uses JWT auth to bypass RLS issues with direct Supabase calls
  const loadConversationHistory = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      console.log('🔍 BillContext: No user ID for loading history');
      return false;
    }

    // Wait for session to be ready before making API calls
    if (!sessionReady || !session?.access_token) {
      console.log('📚 BillContext: Waiting for session to load conversation history...');
      return false;
    }

    try {
      setIsLoadingHistory(true);
      console.log('📚 BillContext: Loading conversation history via API...');

      const response = await fetch('https://mobile.padrinho.app/api/bill/conversations?limit=100', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        console.error('❌ BillContext: API error loading conversation history:', response.status);
        return false;
      }

      const result = await response.json();

      if (!result.success || !result.conversations || result.conversations.length === 0) {
        console.log('📱 BillContext: No conversation history found');
        setHasHistoricalConversations(false);
        return true; // API call succeeded — just no history yet
      }

      // API returns conversations in DESC order (newest first), reverse for chronological display
      const conversations = [...result.conversations].reverse();
      
      // Get the cleared_at timestamp from the response (if available)
      const clearedAt = result.conversation_cleared_at ? new Date(result.conversation_cleared_at) : null;

      // Convert API conversations to message format, filtering by cleared_at for display
      const historyMessages: ConversationMessage[] = [];
      conversations.forEach((conv: any, index: number) => {
        const baseTimestamp = new Date(conv.created_at).getTime();
        const messageDate = new Date(conv.created_at);
        
        // Skip messages created before the cleared_at timestamp (for UI visibility only)
        if (clearedAt && messageDate < clearedAt) {
          return;
        }

        // User message — skip for proactive-only rows (Bill-initiated, no reply yet)
        if (conv.user_message !== '__proactive__') {
          historyMessages.push({
            id: `history_user_${index}`,
            content: conv.user_message,
            role: 'user',
            timestamp: new Date(baseTimestamp),
          });
        }

        // Assistant message — split by ||| so multi-bubble responses render
        // the same way as live session messages (one card per segment)
        const segments = conv.bill_response
          .split('|||')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0);

        segments.forEach((segment: string, segIndex: number) => {
          historyMessages.push({
            id: `history_assistant_${index}_${segIndex}`,
            content: segment,
            role: 'assistant',
            timestamp: new Date(baseTimestamp + 1000 + segIndex),
          });
        });
      });

      setConversationHistory(historyMessages);
      setHasHistoricalConversations(historyMessages.length > 0);

      console.log('✅ BillContext: Loaded conversation history via API', {
        conversationCount: result.conversations.length,
        visibleMessageCount: historyMessages.length,
        clearedAt: clearedAt?.toISOString() || 'none',
        oldestVisible: conversations[0]?.created_at,
        newestVisible: conversations[conversations.length - 1]?.created_at
      });

      return true;
    } catch (error) {
      console.error('❌ BillContext: Error loading conversation history:', error);
      return false;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user?.id, session?.access_token, sessionReady]);

  // Save conversation to AsyncStorage and trigger async Supabase save
  const saveConversation = useCallback(async (messagesToSave: ConversationMessage[]) => {
    if (!user?.id) {
      console.warn('⚠️ BillContext: Cannot save conversation - no user ID');
      return;
    }

    try {
      // Save to AsyncStorage
      const serializedMessages = JSON.stringify(messagesToSave);
      await AsyncStorage.setItem(
        `${STORAGE_KEY}_${user.id}`,
        serializedMessages
      );
      console.log('✅ BillContext: Conversation saved to AsyncStorage', {
        messageCount: messagesToSave.length,
        storageKey: `${STORAGE_KEY}_${user.id}`
      });

      // Find the most recent complete conversation exchange (user -> assistant sequence)
      const conversationExchange = findLatestConversationExchange(messagesToSave);

      if (conversationExchange) {
        const { userMessage, assistantMessages, exchangeStartIndex } = conversationExchange;

        // Combine multiple assistant messages into one response
        const combinedBillResponse = assistantMessages.map(msg => msg.content).join('\n\n');

        console.log('💾 BillContext: Queuing conversation exchange for background save:', {
          userId: user.id,
          userMessagePreview: userMessage.content.substring(0, 50) + '...',
          assistantMessageCount: assistantMessages.length,
          combinedResponsePreview: combinedBillResponse.substring(0, 100) + '...',
          userMessageLength: userMessage.content.length,
          combinedResponseLength: combinedBillResponse.length,
          timestamp: new Date().toISOString(),
          tableName: 'bill_conversations',
          exchangeStartIndex
        });

        // Save conversation asynchronously without blocking UI (via API with JWT auth)
        if (session?.access_token) {
          conversationSaveQueue.push({
            user_id: user.id,
            user_message: userMessage.content,
            bill_response: combinedBillResponse,
            created_at: new Date().toISOString(),
            access_token: session.access_token
          });
          processConversationSaveQueue(); // Trigger processing
        } else {
          console.warn('⚠️ BillContext: No access token available, skipping conversation save');
        }

        // Also save individual messages for backup/redundancy
        const latestMessage = messagesToSave[messagesToSave.length - 1];
        if (latestMessage && (latestMessage.role === 'user' || latestMessage.role === 'assistant')) {
          console.log('💾 BillContext: Latest message details:', {
            id: latestMessage.id,
            role: latestMessage.role,
            contentLength: latestMessage.content.length,
            timestamp: latestMessage.timestamp
          });
        }
      } else {
        console.log('⚠️ BillContext: No complete conversation exchange found to save:', {
          messageCount: messagesToSave.length,
          lastFewMessages: messagesToSave.slice(-5).map(m => ({
            role: m.role,
            id: m.id,
            preview: m.content.substring(0, 30) + '...'
          }))
        });
      }

      // Auto-summarize long conversations to prevent token limits
      if (messagesToSave.length >= 20) { // Summarize after 20 messages
        try {
          console.log('🔄 BillContext: Triggering auto-summarization', {
            messageCount: messagesToSave.length,
            userId: user.id
          });

          // Add user ID context to messages before summarization
          const messagesWithUserContext = messagesToSave.map(msg => ({
            ...msg,
            userId: user.id
          }));

          await conversationSummaryService.summarizeLongConversation(messagesWithUserContext);
        } catch (summaryError) {
          console.error('❌ Auto-summarization failed:', summaryError);
        }
      }

      // Generate session summary if we have enough messages
      if (messagesToSave.length >= 6) { // At least 3 exchanges
        try {
          // Get current sobriety days for context via API
          let currentSobrietyDays = 0;
          try {
            const { sobrietyService } = await import('@/services/sobrietyService');
            const sobrietyRecord = await sobrietyService.getSobrietyRecord();
            if (sobrietyRecord) {
              currentSobrietyDays = sobrietyRecord.currentDays || 0;
            }
          } catch (sobrietyError) {
            console.warn('⚠️ Could not fetch sobriety data for summary:', sobrietyError);
          }

          // Generate and save summary
          const summary = await conversationSummaryService.generateSessionSummary(
            user.id,
            messagesToSave,
            currentSobrietyDays
          );

          await conversationSummaryService.saveSummary(summary);

          console.log('✅ BillContext: Conversation summary generated and saved:', {
            messageCount: summary.message_count,
            topics: summary.topics_discussed,
            sentiment: summary.user_sentiment,
            supportTypes: summary.support_types,
            keyMomentsCount: summary.key_moments.length
          });

        } catch (summaryError) {
          console.error('❌ BillContext: Error generating conversation summary:', summaryError);
          // Don't throw error to avoid breaking the conversation flow
        }
      }

    } catch (error) {
      console.error('❌ BillContext: Error saving conversation:', error);
      // Don't throw error to avoid breaking the conversation flow
    }
  }, [user?.id, session?.access_token]);

  // Load stored messages from AsyncStorage
  const loadStoredMessages = useCallback(async (): Promise<ConversationMessage[]> => {
    if (!user?.id) {
      console.log('🔍 BillContext: No user ID, returning empty messages');
      return [];
    }

    try {
      const storageKey = `${STORAGE_KEY}_${user.id}`;
      const stored = await AsyncStorage.getItem(storageKey);

      if (stored) {
        const parsed = JSON.parse(stored);
        const messages = parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        console.log('✅ BillContext: Loaded stored messages', {
          messageCount: messages.length,
          storageKey
        });
        return messages;
      } else {
        console.log('📱 BillContext: No stored messages found for user', { storageKey });
      }
    } catch (error) {
      console.error('❌ BillContext: Error loading stored messages:', error);
    }
    return [];
  }, [user?.id]);

  // Initialize the context - called only from the initialization useEffect
  // The useEffect handles the guard logic and sets initializationRef.current before calling
  const initializeContext = useCallback(async () => {
    if (!user?.id) {
      console.log('⏭️ BillContext: Skipping initialization - no user');
      return;
    }

    // Ensure session is ready before loading conversation history via API
    if (!sessionReady || !session?.access_token) {
      console.log('⏳ BillContext: Waiting for session before initialization...');
      return;
    }

    console.log('📱 BillContext: Running initialization for user:', user.id, 'sessionReady:', sessionReady);

    try {
      setIsLoading(true);
      setError(null);

      // Load conversation history from the server (source of truth)
      const historyLoaded = await loadConversationHistory();

      if (historyLoaded) {
        // Server history loaded successfully — clear AsyncStorage so stale
        // session messages don't create duplicates. History covers everything
        // already saved; new messages this session will be added fresh.
        const storageKey = `${STORAGE_KEY}_${user.id}`;
        await AsyncStorage.removeItem(storageKey);
        console.log('🧹 BillContext: Cleared AsyncStorage after successful history load (server is source of truth)');
      } else {
        // Server unreachable — fall back to AsyncStorage so the user still
        // sees their recent messages while offline.
        const storedMessages = await loadStoredMessages();
        if (storedMessages.length > 0) {
          setMessages(storedMessages);
          console.log('📱 BillContext: Offline fallback — loaded stored messages from AsyncStorage', { count: storedMessages.length });
        }
      }

      // Initialize conversation service context with full history
      await conversationService.initializeContext(user.id);

      console.log('✅ BillContext: Initialization completed successfully');
    } catch (error) {
      console.error('❌ BillContext: Error initializing context:', error);
      setError(error instanceof Error ? error.message : 'Failed to initialize chat');
      throw error; // Re-throw to let the useEffect handle ref reset
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, sessionReady, session?.access_token, loadStoredMessages, loadConversationHistory]);

  // SIMPLIFIED: Send message directly - NO BATCHING
  const sendMessage = useCallback(async (message: string, specificGuidance?: string, sobrietyDays?: number) => {
    if (!user?.id || !message.trim() || isLoading) {
      console.warn('Cannot send message: no user, empty message, or already loading', {
        hasUser: !!user?.id,
        userId: user?.id,
        hasMessage: !!message?.trim(),
        messagePreview: message?.substring(0, 20),
        isLoading,
        isInitialized,
        sessionReady
      });
      return;
    }
    
    // Check for session readiness - essential for authenticated API calls
    if (!sessionReady || !session?.access_token) {
      console.warn('⚠️ BillContext: Cannot send message - session not ready or no token', {
        sessionReady,
        hasToken: !!session?.access_token
      });
      setError('Aguarde a sessão inicializar antes de enviar mensagens');
      return;
    }

    // Note: Message limit is enforced server-side. The frontend uses remainingMessages 
    // from server responses as the source of truth. The dailyLimitReached flag is only
    // used for UI display purposes, not for blocking sends.

    const messageContent = message.trim();
    console.log('📝 BillContext: User sent message - DIRECT PROCESSING', {
      userId: user.id,
      messageLength: messageContent.length,
      messagePreview: messageContent.substring(0, 50) + '...',
      specificGuidance
    });

    setIsLoading(true);
    setError(null);

    // Add user message to UI immediately
    const userMessage: ConversationMessage = {
      id: `user_${Date.now()}`,
      content: messageContent,
      role: 'user',
      timestamp: new Date(),
    };

    // Mark first_message onboarding action complete (fire and forget)
    onboardingService.markComplete('first_message').catch(() => {});

    // PRD §5.3 — micro event. Use length_bucket (dictionary key) so we never
    // expose raw character counts that could correlate with sensitive content.
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent, lengthBucket }) =>
        logClarityEvent('bill_message_sent', {
          length_bucket: lengthBucket(messageContent.length),
          message_ix_in_session: userSentCountRef.current + 1,
        })
      )
      .catch(() => {});

    // PRD §4 — `bill_first_message_sent` macro event. Idempotent across
    // sessions via AsyncStorage flag inside the helper.
    import('@/utils/clarityEvents')
      .then(({ logBillFirstMessageSent }) =>
        logBillFirstMessageSent({ from_surface: 'bill_chat' })
      )
      .catch(() => {});

    // Track how many messages the user has sent this session (used for memory summarize trigger)
    userSentCountRef.current += 1;

    // If a previous response is still revealing balloon-by-balloon, flush the
    // remainder NOW so they land before this new user message — preserving order
    // and ensuring the next eager persist captures the complete prior exchange.
    flushPendingReveal();

    setMessages(prev => {
      const updatedMessages = [...prev, userMessage];
      // Process message directly with current conversation context
      processMessageDirectly(messageContent, updatedMessages, specificGuidance, sobrietyDays);
      return updatedMessages;
    });

  }, [user?.id, isLoading, sessionReady, session?.access_token]);

  // SIMPLIFIED: Direct message processing - no batching
  const processMessageDirectly = useCallback(async (messageContent: string, currentMessages: ConversationMessage[], specificGuidance?: string, sobrietyDays?: number) => {
    if (!user?.id) return;

    const startedAt = Date.now();
    let statusCode: number | undefined;

    try {
      // Prepare conversation history with proper format for continuity
      const conversationHistory = currentMessages
        .slice(-10) // Last 10 messages for context  
        .map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));

      console.log('📡 BillContext: Sending to server with conversation history', {
        historyLength: conversationHistory.length,
        lastMessages: conversationHistory.map(m => `${m.role}: ${m.content.substring(0, 30)}...`)
      });

      // Direct server call with conversation history for continuity
      // FIX: Force production API - abandon all localhost usage
      const apiUrl = 'https://mobile.padrinho.app';
      
      // Log if we're overriding a localhost configuration
      const envUrl = process.env.EXPO_PUBLIC_API_URL;
      if (envUrl && (
        envUrl.includes('localhost') || 
        envUrl.includes('127.0.0.1') || 
        envUrl.startsWith('http://10.') || 
        envUrl.startsWith('http://192.168.')
      )) {
        console.log('🔧 BillContext: Forcing production API, overriding local URL:', envUrl, '→', apiUrl);
      }
      
      // Map language codes to locale codes
      const localeMap: Record<string, string> = {
        'pt': 'pt-BR',
        'en': 'en-US',
        'es': 'es-ES'
      };
      const userLocale = localeMap[language] || 'pt-BR';

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        console.log('🔐 BillContext: Including JWT token for authenticated request');
      } else {
        console.warn('⚠️ BillContext: No session token available for request');
      }

      const serverResponse = await fetch(`${apiUrl}/api/bill/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: messageContent,
          userId: user.id,
          conversationHistory: conversationHistory, // Key for continuity!
          specificGuidance: specificGuidance, // Pass specific guidance for suggestions
          language: userLocale, // Pass user's language preference
          // Canonical device-local sobriety day count (utils/sobrietyDays.ts) so
          // Bill's server prompt states the SAME number the app UI shows.
          ...(typeof sobrietyDays === 'number' && sobrietyDays >= 0
            ? { context: { sobrietyDays } }
            : {}),
        }),
      });

      statusCode = serverResponse.status;

      if (!serverResponse.ok) {
        throw new Error(`Server error: ${serverResponse.status} ${serverResponse.statusText}`);
      }

      const response = await serverResponse.json();
      
      // Update remaining messages from server response (accurate server-side count)
      if (typeof response.remainingMessages === 'number' && response.remainingMessages >= 0) {
        console.log('📊 BillContext: Updating remaining messages from server:', response.remainingMessages);
        setRemainingMessages(response.remainingMessages);
      }

      // Check if daily limit was reached (from server response - source of truth)
      if (response.limitReached) {
        console.log('⚠️ BillContext: Daily message limit reached (server)');
        setDailyLimitReached(true);
        setRemainingMessages(0);

        // Fire-and-forget Clarity event for plan-gating funnel visibility.
        // Only non-PII metadata (LGPD).
        import('@/utils/clarityEvents')
          .then(({ logClarityEvent }) =>
            logClarityEvent('bill_message_limit_reached', {
              provider: response.provider || 'unknown',
            })
          )
          .catch(() => {});
      }
      
      console.log('✅ BillContext: Got server response with context', {
        messageLength: response.message?.length || 0,
        provider: response.provider || 'unknown',
        hasMessage: !!response.message,
        supportType: response.supportType
      });

      if (!response.message || response.message.trim().length === 0) {
        throw new Error('Empty response from server');
      }

      // PRD §5.3 — `bill_response_received` micro event. Provider is a
      // server-set enum; latency_ms is numeric. None of these reveal message
      // content (LGPD). Emitted after the empty-response guard so an empty
      // response is tagged only by `bill_response_failed`, keeping
      // success-rate analytics accurate.
      const billLatencyMs = Date.now() - startedAt;
      import('@/utils/clarityEvents')
        .then(({ logBillResponseReceived }) =>
          logBillResponseReceived({
            latency_ms: billLatencyMs,
            provider: response.provider || 'unknown',
          })
        )
        .catch(() => {});

      // Split Bill's response for natural conversation flow
      const billMessages = splitBillResponse(response.message);

      // Create assistant messages
      const assistantMessages: ConversationMessage[] = billMessages.map((content, i) => ({
        id: `assistant_${Date.now()}_${i}`,
        content: content,
        role: 'assistant',
        timestamp: new Date(Date.now() + i * 100),
        context: {
          supportType: response.supportType || 'general'
        }
      }));

      console.log('✅ BillContext: Created Bill response messages', {
        messageCount: assistantMessages.length,
        messages: assistantMessages.map(m => ({ id: m.id, content: m.content.substring(0, 50) + '...', role: m.role }))
      });

      // Reset daily limit flag on successful response (in case user upgraded mid-session)
      if (dailyLimitReached) {
        console.log('🔄 BillContext: Resetting daily limit flag after successful message');
        setDailyLimitReached(false);
      }

      // Persist the FULL exchange eagerly — BEFORE the staggered UI reveal — so
      // the complete response is never lost if the app is backgrounded / killed
      // mid-reveal. `currentMessages` already includes the user message.
      const fullExchange = [...currentMessages, ...assistantMessages];
      saveConversation(fullExchange).catch(error => {
        console.error('❌ BillContext: Error saving conversation:', error);
      });

      // Fire-and-forget: update persistent context once the full exchange is received.
      const persistUserId = userIdRef.current;
      if (persistUserId) {
        const messagesForContext = fullExchange.map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp
        }));
        contextPersistenceService.updateContextFromConversation(persistUserId, messagesForContext).catch(err => {
          console.warn('⚠️ BillContext: Persistent context update failed (non-critical):', err?.message);
        });
      }

      // Reveal assistant balloons one-by-one for a natural feel (UI only).
      revealAssistantMessages(assistantMessages);

      // RJ-3b — invalidate today's recovery plan so Bill-driven activity
      // (mood logs, mission nudges acted on mid-chat) is reflected in the
      // Missões surface without waiting for a tab focus. Hooked here — on
      // the success branch after `serverResponse.ok` + non-empty response
      // validation — so failed sends do NOT trigger spurious invalidations.
      // Dynamic import matches the pattern used by the other writer hooks.
      import('@/services/recoveryPlanService')
        .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
        .catch(() => {});

      // Fire-and-forget: summarize memory every 8 user messages (Plus-only, server handles gating)
      if (userSentCountRef.current >= 8 && userSentCountRef.current % 8 === 0) {
        const tokenForMemory = session?.access_token;
        if (tokenForMemory) {
          const messagesForMemory = currentMessages
            .slice(-24)
            .map((m) => ({ role: m.role, content: m.content }));
          fetch('https://mobile.padrinho.app/api/bill/memory/summarize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokenForMemory}`,
            },
            body: JSON.stringify({ conversationMessages: messagesForMemory }),
          })
            .then((r) => r.json())
            .then((result) => {
              console.log('🧠 BillContext: Memory summarize -', result.success ? 'saved' : result.reason);
            })
            .catch((err) => {
              console.warn('⚠️ BillContext: Memory summarize failed (non-critical):', err.message);
            });
        }
      }

    } catch (error) {
      console.error('❌ BillContext: Error processing message:', error);
      setError(error instanceof Error ? error.message : 'Failed to process message');

      // PRD §5.3 — `bill_response_failed` micro event paired with
      // `bill_response_received` so the dashboard can compute Bill's success
      // rate. Metadata is non-PII (LGPD): a stable error code (HTTP status
      // when known, else the error class name) and the latency. The raw
      // error message is NEVER passed — it can leak server-side detail.
      const errorType =
        error instanceof Error && error.name
          ? error.name
          : typeof error === 'object' && error !== null
            ? 'Object'
            : typeof error;
      const errorCode = statusCode ? String(statusCode) : errorType;
      import('@/utils/clarityEvents')
        .then(({ logBillResponseFailed }) =>
          logBillResponseFailed({
            error_code: errorCode,
            latency_ms: Date.now() - startedAt,
          })
        )
        .catch(() => {});

      // Add fallback error message
      const errorMessage: ConversationMessage = {
        id: `error_${Date.now()}`,
        content: t('bill.errors.technicalIssue'),
        role: 'assistant',
        timestamp: new Date(),
        context: {
          supportType: 'general'
        }
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, saveConversation]); // revealAssistantMessages is defined below (stable callback)

  // Send a direct message (for suggested actions)
  const sendDirectMessage = useCallback(async (messageContent: string, specificGuidance?: string, sobrietyDays?: number) => {
    return sendMessage(messageContent, specificGuidance, sobrietyDays);
  }, [sendMessage]);


  // Check and trigger sobriety milestone congratulations
  const checkSobrietyMilestone = useCallback(async (currentDays: number, previousDays?: number) => {
    if (!user?.id) return;

    try {
      // Check if we achieved a new milestone
      // Only check when days actually increase (not decrease due to relapse)
      if (previousDays !== undefined && currentDays > previousDays) {
        console.log('🎯 BillContext: Checking milestone for days increase', {
          currentDays,
          previousDays,
          userId: user.id
        });

        const achievedMilestone = await sobrietyMilestoneService.checkAndRecordMilestone(
          user.id,
          currentDays,
          previousDays
        );

        if (achievedMilestone) {
          console.log('🎉 BillContext: Sobriety milestone achieved!', {
            milestone: achievedMilestone,
            currentDays,
            previousDays
          });

          // Generate personalized celebration prompt
          const celebrationPrompt = sobrietyMilestoneService.generateCelebrationPrompt(
            achievedMilestone,
            currentDays
          );

          const response: ConversationResponse = await conversationService.sendMessage(user.id, celebrationPrompt, {
            sobrietyDays: achievedMilestone.days,
            currentMood: 'celebration',
            accessToken: session?.access_token
          });

          // Split Bill's response into multiple messages
          const billMessages = splitBillResponse(response.message);

          const celebrationMessages: ConversationMessage[] = billMessages.map((content, i) => ({
            id: `milestone_${Date.now()}_${i}`,
            content,
            role: 'assistant',
            timestamp: new Date(Date.now() + i * 100),
            context: {
              supportType: 'celebration',
              sobrietyDays: currentDays
            }
          }));

          console.log('🎉 BillContext: Milestone celebration messages created', {
            milestone: achievedMilestone.label,
            importance: achievedMilestone.importance,
            totalMessages: celebrationMessages.length
          });

          // Flush any prior in-flight reveal first so an earlier response's
          // pending balloons are part of the snapshot we persist.
          flushPendingReveal();

          // Persist eagerly (decoupled from the UI reveal) so nothing is lost.
          const fullExchange = [...messagesRef.current, ...celebrationMessages];
          saveConversation(fullExchange).catch(error => {
            console.error('❌ BillContext: Error saving milestone conversation:', error);
          });

          // Reveal balloons — longer delays for important milestones.
          const delay = achievedMilestone.importance === 'high' ? 1200 : 800;
          revealAssistantMessages(celebrationMessages, { perMessageDelay: delay });
        }
      }

    } catch (error) {
      console.error('❌ BillContext: Error in milestone checking:', error);
    }
  }, [user?.id, saveConversation]);

  // Get daily encouragement
  const getDailyEncouragement = useCallback(async () => {
    if (!user?.id) return;

    try {
      const encouragementPrompt = `Ofereça uma mensagem de encorajamento diária para alguém em recuperação. Seja positivo, motivador e lembre-os de que cada dia é uma vitória. Use múltiplas mensagens separadas por "|||".`;

      const response: ConversationResponse = await conversationService.sendMessage(user.id, encouragementPrompt, {
        currentMood: 'seeking_encouragement',
        accessToken: session?.access_token
      });

      const billMessages = splitBillResponse(response.message);

      const encouragementMessages: ConversationMessage[] = billMessages.map((content, i) => ({
        id: `daily_${Date.now()}_${i}`,
        content,
        role: 'assistant',
        timestamp: new Date(Date.now() + i * 100),
        context: {
          supportType: 'encouragement'
        }
      }));

      // Flush any prior in-flight reveal first so an earlier response's
      // pending balloons are part of the snapshot we persist.
      flushPendingReveal();

      // Persist eagerly (decoupled from the UI reveal) so nothing is lost.
      const fullExchange = [...messagesRef.current, ...encouragementMessages];
      saveConversation(fullExchange).catch(error => {
        console.error('❌ BillContext: Error saving daily encouragement:', error);
      });

      revealAssistantMessages(encouragementMessages, { perMessageDelay: 800 });

    } catch (error) {
      console.error('❌ BillContext: Error generating daily encouragement:', error);
    }
  }, [user?.id, saveConversation]);

  // Get crisis support
  const getCrisisSupport = useCallback(async () => {
    if (!user?.id) return;

    try {
      const crisisPrompt = `O usuário passou por uma recaída. Ofereça apoio compassivo, sem julgamento. Lembre-o que recaídas fazem parte do processo de recuperação e que ele pode recomeçar. Seja acolhedor e oferece esperança. Use múltiplas mensagens separadas por "|||".`;

      const response: ConversationResponse = await conversationService.sendMessage(user.id, crisisPrompt, {
        currentMood: 'crisis',
        accessToken: session?.access_token
      });

      const billMessages = splitBillResponse(response.message);

      const supportMessages: ConversationMessage[] = billMessages.map((content, i) => ({
        id: `crisis_${Date.now()}_${i}`,
        content,
        role: 'assistant',
        timestamp: new Date(Date.now() + i * 100),
        context: {
          supportType: 'crisis'
        }
      }));

      // Flush any prior in-flight reveal first so an earlier response's
      // pending balloons are part of the snapshot we persist.
      flushPendingReveal();

      // Persist eagerly (decoupled from the UI reveal) so nothing is lost.
      const fullExchange = [...messagesRef.current, ...supportMessages];
      saveConversation(fullExchange).catch(error => {
        console.error('❌ BillContext: Error saving crisis support:', error);
      });

      revealAssistantMessages(supportMessages, { perMessageDelay: 800 });

    } catch (error) {
      console.error('❌ BillContext: Error generating crisis support:', error);
    }
  }, [user?.id, saveConversation]);

  // Update typing activity
  const updateTypingActivity = useCallback(() => {
    setIsTyping(true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set typing to false after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 2000) as unknown as NodeJS.Timeout;
  }, []);

  // Immediately render any balloons that are still waiting to be revealed.
  // Called when the app is backgrounded mid-reveal so suspended JS timers can
  // never leave later balloons stranded off-screen.
  const flushPendingReveal = useCallback(() => {
    const pending = pendingRevealRef.current;
    if (!pending) return;
    if (pending.timer) {
      clearTimeout(pending.timer);
    }
    const remaining = pending.remaining;
    pendingRevealRef.current = null;
    if (remaining.length > 0) {
      console.log(`⏩ BillContext: Flushing ${remaining.length} pending balloon(s) immediately`);
      setMessages(prev => [...prev, ...remaining]);
    }
  }, []);

  // Reveal Bill's balloons one-by-one for a natural conversation feel.
  // This is a UI concern ONLY — persistence is handled eagerly by the caller so
  // the full exchange is never lost if the reveal is interrupted.
  const revealAssistantMessages = useCallback((
    assistantMessages: ConversationMessage[],
    options?: { perMessageDelay?: number }
  ) => {
    if (!assistantMessages || assistantMessages.length === 0) return;

    // Cancel any in-flight reveal, flushing its remainder first so nothing is dropped.
    flushPendingReveal();

    const first = assistantMessages[0]!;
    const rest = assistantMessages.slice(1);
    setMessages(prev => [...prev, first]);

    if (rest.length === 0) {
      pendingRevealRef.current = null;
      return;
    }

    pendingRevealRef.current = { remaining: [...rest], timer: null };

    const scheduleNext = (prevContentLength: number) => {
      const pending = pendingRevealRef.current;
      if (!pending || pending.remaining.length === 0) {
        pendingRevealRef.current = null;
        return;
      }
      // 30ms per char, min 1s, max 3s — simulates typing time.
      const delay = options?.perMessageDelay
        ?? Math.min(Math.max(prevContentLength * 30, 1000), 3000);
      pending.timer = setTimeout(() => {
        const p = pendingRevealRef.current;
        if (!p || p.remaining.length === 0) {
          pendingRevealRef.current = null;
          return;
        }
        const next = p.remaining.shift()!;
        setMessages(prev => [...prev, next]);
        scheduleNext(next.content.length);
      }, delay) as unknown as ReturnType<typeof setTimeout>;
    };

    scheduleNext(first.content.length);
  }, [flushPendingReveal]);

  // Flush pending balloons when the app is backgrounded so suspended timers
  // never strand later balloons. (Persistence already happened eagerly.)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'background' || nextAppState === 'inactive') {
        flushPendingReveal();
      }
    });
    return () => {
      subscription.remove();
    };
  }, [flushPendingReveal]);

  // Memory management methods
  const stopPeriodicCleanup = useCallback(() => {
    if (cleanupIntervalRef.current) {
      clearInterval(cleanupIntervalRef.current);
      cleanupIntervalRef.current = null;
      console.log('🛑 BillContext: Stopped periodic memory cleanup');
    }
  }, []);

  const startPeriodicCleanup = useCallback(() => {
    if (cleanupIntervalRef.current) {
      clearInterval(cleanupIntervalRef.current);
    }

    cleanupIntervalRef.current = setInterval(async () => {
      try {
        console.log('🧹 BillContext: Performing periodic memory cleanup...');
        // await conversationService.performGlobalCleanup(); // Method not available

        // Also clean up local message state if it gets too large
        setMessages(prevMessages => {
          if (prevMessages.length > MAX_STORED_MESSAGES) {
            const trimmed = prevMessages.slice(-Math.floor(MAX_STORED_MESSAGES * 0.8));
            console.log(`🧹 BillContext: Trimmed local messages from ${prevMessages.length} to ${trimmed.length}`);
            return trimmed;
          }
          return prevMessages;
        });
      } catch (error) {
        console.error('❌ BillContext: Error during periodic cleanup:', error);
      }
    }, CLEANUP_INTERVAL) as unknown as NodeJS.Timeout;

    console.log('✅ BillContext: Started periodic memory cleanup');
    return cleanupIntervalRef.current; // Return the interval ID
  }, []);

  const saveConversationSummaryToDatabase = useCallback(async () => {
    if (!user?.id || messages.length === 0) {
      console.warn('BillContext: No user ID or messages available for saving summary');
      return;
    }

    try {
      // Generate conversation summary from current messages
      const conversationMessages = messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp,
        context: msg.context
      }));

      const summary = await conversationSummaryService.generateSessionSummary(
        user.id,
        conversationMessages,
        // Get current sobriety days (you'll need to import this from SobrietyContext)
        undefined // We'll get this from context if available
      );

      // Save session summary to bill_persistent_context via the server API
      console.log('💾 BillContext: Saving session summary via API...', {
        userId: user.id,
        messageCount: summary.message_count,
        topics: summary.topics_discussed,
        sentiment: summary.user_sentiment
      });

      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const apiResponse = await fetch('/api/conversations/summary', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            session_date: summary.session_date,
            message_count: summary.message_count,
            topics_discussed: summary.topics_discussed,
            user_sentiment: summary.user_sentiment,
            support_types: summary.support_types,
            key_moments: summary.key_moments,
            sobriety_days_at_time: summary.sobriety_days_at_time
          })
        });
        const apiResult = await apiResponse.json();
        if (apiResult.success) {
          console.log('✅ BillContext: Session summary saved to bill_persistent_context:', apiResult.action);
        } else {
          console.error('❌ BillContext: API error saving session summary:', apiResult.error);
        }

        // Fire-and-forget: regenerate period digests (7d/14d/30d) from accumulated session_summaries
        // All users — server handles Plus gating for the 30-day bucket
        if (token) {
          fetch('https://mobile.padrinho.app/api/bill/memory/digest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({})
          })
            .then(r => r.json())
            .then(result => {
              console.log('📅 BillContext: Memory digest -', result.success ? `tiers: ${result.tiers?.join(', ')}` : result.reason);
            })
            .catch(err => {
              console.warn('⚠️ BillContext: Memory digest failed (non-critical):', err.message);
            });
        }
      } catch (fetchError) {
        console.error('❌ BillContext: Error saving session summary via API:', fetchError);
      }
    } catch (error) {
      console.error('❌ BillContext: Unexpected error saving conversation summary:', error);
    }
  }, [user?.id, messages.length]);

  // Clear conversation history (visibility only - Bill retains context)
  const clearConversation = useCallback(async () => {
    // Save final session summary before clearing
    if (messages.length > 0) {
      await saveConversationSummaryToDatabase();
    }

    // Clear response timeout
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    // REMOVED: setPendingMessages - no longer using batching system
    setIsBatchProcessing(false);

    // Clear both session messages AND conversation history (UI visibility)
    setMessages([]);
    setConversationHistory([]);
    pendingBatchRef.current = [];
    await AsyncStorage.removeItem(`${STORAGE_KEY}_${user?.id}`);

    if (user?.id) {
      await conversationService.clearContext(user.id);
    }
    
    // Set conversation_cleared_at timestamp on server to hide old messages
    if (session?.access_token) {
      try {
        const response = await fetch('https://mobile.padrinho.app/api/bill/clear', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        
        if (!response.ok) {
          console.error('❌ BillContext: Failed to set cleared_at timestamp:', response.status);
        } else {
          console.log('✅ BillContext: conversation_cleared_at timestamp set on server');
        }
      } catch (error) {
        console.error('❌ BillContext: Error setting cleared_at timestamp:', error);
      }
    }
    
    console.log('✅ BillContext: Conversation cleared (session + history visibility)');
  }, [user?.id, messages.length, saveConversationSummaryToDatabase, session?.access_token]);

  // Initialize context when user changes - MUST wait for sessionReady AND access_token
  useEffect(() => {
    // CRITICAL: Wait for sessionReady AND access_token before initializing to ensure API calls work
    if (user?.id && sessionReady && session?.access_token && !initializationRef.current) {
      console.log('🚀 BillContext: Starting initialization for user:', user?.id, 'sessionReady:', sessionReady, 'hasToken:', !!session?.access_token);

      // Set ref immediately to prevent duplicate initialization
      initializationRef.current = true;

      // Initialize context and start cleanup - set isInitialized AFTER success
      initializeContext().then(() => {
        // Only set initialized after successful initialization
        setIsInitialized(true);
        console.log('✅ BillContext: Initialization completed, isInitialized set to true');
        // Start periodic cleanup after successful initialization
        startPeriodicCleanup();
      }).catch(error => {
        console.error('❌ BillContext: Failed to initialize:', error);
        // Reset ref on error to allow retry
        initializationRef.current = false;
        setIsInitialized(false);
      });

    } else if (user?.id && (!sessionReady || !session?.access_token)) {
      // User available but session not ready yet - wait
      console.log('⏳ BillContext: User available but waiting for session validation...', {
        userId: user?.id,
        sessionReady,
        hasToken: !!session?.access_token
      });
    } else if (!user?.id) {
      // Reset state when user logs out - CRITICAL: reset ref too
      setMessages([]);
      setConversationHistory([]);
      setIsInitialized(false);
      setError(null);
      initializationRef.current = false; // CRITICAL: reset ref for next login
      pendingBatchRef.current = [];
      stopPeriodicCleanup(); // Stop cleanup when user logs out
    }
  }, [user?.id, sessionReady, session?.access_token, initializeContext, startPeriodicCleanup, stopPeriodicCleanup]);

    // Debug effect for EAS builds
  useEffect(() => {
    console.log('🔍 BillContext: State change detected', {
      messageCount: messages.length,
      isInitialized,
      isLoading,
      error: !!error,
      userId: user?.id,
      timestamp: new Date().toISOString()
    });
  }, [messages.length, isInitialized, isLoading, error, user?.id]);

  // Cleanup effect
  useEffect(() => {
    if (!user?.id) return;

    const cleanup = async () => {
      try {
        // Only run cleanup if we have the cleanup service available
        // memoryMonitorService is not available - skip cleanup
        console.log('⚠️ BillContext: Cleanup service not available (memoryMonitorService undefined)');

        // Always try to run summarization if we have enough messages
        if (messages.length >= 20) {
          console.log('🔄 BillContext: Running periodic summarization check...');
          try {
            await conversationSummaryService.summarizeLongConversation(
              messages.map(msg => ({ ...msg, userId: user?.id }))
            );
          } catch (error) {
            console.error('❌ BillContext: Periodic summarization failed:', error);
          }
        }
      } catch (error) {
        console.error('❌ BillContext: Periodic cleanup error:', error);
      }
    };

    // Run cleanup every 30 minutes
    const cleanupInterval = setInterval(cleanup, 30 * 60 * 1000);

    // Run initial cleanup after 5 minutes
    const initialCleanup = setTimeout(cleanup, 5 * 60 * 1000);

    console.log('✅ BillContext: Started periodic memory cleanup');

    return () => {
      clearInterval(cleanupInterval);
      clearTimeout(initialCleanup);
      console.log('🧹 BillContext: Stopped periodic memory cleanup');
    };
  }, [user?.id, messages.length]);

  

  // Check if daily limit has expired by refreshing from server (source of truth)
  // Returns true if limit is still active, false if expired/cleared
  const checkDailyLimitExpiration = useCallback(async (): Promise<boolean> => {
    if (!user?.id) return dailyLimitReached;
    
    // Refresh from server to get accurate state
    const result = await fetchMessageUsageFromServer();
    return result?.limitReached ?? dailyLimitReached;
  }, [user?.id, dailyLimitReached, fetchMessageUsageFromServer]);

  // Reset daily limit by refreshing from server (for subscription changes)
  const resetDailyLimit = useCallback(async () => {
    if (!user?.id) {
      console.warn('⚠️ BillContext: Cannot reset daily limit without user ID');
      return;
    }
    
    console.log('🔄 BillContext: Refreshing message usage from server');
    await fetchMessageUsageFromServer();
    await billConfigCacheService.invalidateAllConfigCaches();
  }, [user?.id, fetchMessageUsageFromServer]);

  // Listen for subscription changes to immediately update Bill context
  useEffect(() => {
    if (!user?.id) return;

    const subscriptionListener = DeviceEventEmitter.addListener(
      'subscription:status-changed',
      async (event: { hasPadrinhoPlus: boolean; remainingMessages?: number; limitReached?: boolean }) => {
        console.log('🎯 BillContext: Subscription status changed via event', {
          hasPadrinhoPlus: event.hasPadrinhoPlus,
          serverRemainingMessages: event.remainingMessages,
          serverLimitReached: event.limitReached,
        });

        if (event.hasPadrinhoPlus) {
          // User upgraded to Plus - unlimited messages
          console.log('🔓 BillContext: User upgraded to Plus - unlimited messages');
          setDailyLimitReached(false);
          setRemainingMessages(-1); // Unlimited
        } else {
          // User is Base - use server's message usage data directly
          console.log('📉 BillContext: User is Base - applying server limit state');
          setDailyLimitReached(event.limitReached ?? false);
          setRemainingMessages(event.remainingMessages ?? 30);
        }
        
        // Invalidate Bill config caches
        await billConfigCacheService.invalidateAllConfigCaches();
      }
    );

    console.log('✅ BillContext: Subscribed to subscription change events');

    return () => {
      subscriptionListener.remove();
      console.log('🧹 BillContext: Unsubscribed from subscription change events');
    };
  }, [user?.id]);

  const combinedMessages = useMemo(() => {
    const messageMap = new Map<string, ConversationMessage>();

    // History messages go in first — they are the source of truth
    conversationHistory.forEach(msg => {
      if (msg.id) {
        messageMap.set(msg.id, msg);
      }
    });

    // Build a set of content fingerprints already covered by history so we
    // can skip session messages that were already saved to the server and
    // returned in the next history load (they would have different IDs but
    // identical role + content, causing duplicates).
    const historyCovered = new Set<string>(
      conversationHistory.map(m => `${m.role}:${m.content}`)
    );

    // Session messages (AsyncStorage) — only add if not already in history
    messages.forEach(msg => {
      if (msg.id && !historyCovered.has(`${msg.role}:${msg.content}`)) {
        messageMap.set(msg.id, msg);
      }
    });

    const combined = Array.from(messageMap.values()).sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });

    console.log('📊 BillContext: Combined messages:', {
      historyCount: conversationHistory.length,
      sessionCount: messages.length,
      combinedCount: combined.length,
      deduped: conversationHistory.length + messages.length - combined.length
    });

    return combined;
  }, [conversationHistory, messages]);

  const value: BillContextType = {
    messages: combinedMessages,
    conversationHistory,
    isLoading,
    isLoadingHistory,
    error,
    dailyLimitReached,
    remainingMessages,
    sendMessage,
    sendDirectMessage,
    loadConversationHistory,
    clearConversation,
    updateTypingActivity,
    isInitialized,
    hasHistoricalConversations,
    isTyping,
    isBatchProcessing,
    checkSobrietyMilestone,
    getDailyEncouragement,
    getCrisisSupport,
    resetDailyLimit,
    checkDailyLimitExpiration,
  };

  return (
    <BillContext.Provider value={value}>
      {children}
    </BillContext.Provider>
  );
}

export function useBill() {
  const context = useContext(BillContext);
  if (context === undefined) {
    throw new Error('useBill must be used within a BillProvider');
  }
  return context;
}